import React, { useState, useEffect, useCallback } from 'react';
import { Box, TextField, MenuItem, Divider, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useSnackbar } from 'notistack';
import * as groupApi from '../../../lib/api/cstGroup';
import GroupFormDialog from './GroupFormDialog';

const NONE = '__none__';
const NEW = '__new__';

/**
 * 기회가 어느 묶음에 속하는지 고르는 칸.
 *
 * 목록 맨 아래에 "새 묶음 만들기" 를 둔 이유는, 묶을 생각이 기회를 적다가 나기 때문이다.
 * 목록 화면으로 나갔다 오게 하면 쓰다 만 내용이 날아간다.
 *
 * 고르는 즉시 저장한다. 기회 폼의 저장 버튼과 엮지 않은 건 소속이 기회의 다른 칸들과
 * 다른 성격이어서다 - 분류를 바꾸는 것뿐이고, 되돌리기도 쉽다.
 */
const GroupPicker = ({ prjUuid, value, onChanged, disabled }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState(value || NONE);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await groupApi.getGroups({ searchStatus: 'OPEN' });
      setGroups(res.data?.list || []);
    } catch {
      setGroups([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelected(value || NONE); }, [value]);

  const apply = async (groupUuid) => {
    if (!prjUuid) return;                 // 아직 저장 안 된 새 기회
    try {
      await groupApi.assignChance(prjUuid, groupUuid === NONE ? '' : groupUuid);
      enqueueSnackbar(groupUuid === NONE ? '소속을 풀었습니다.' : '묶음에 넣었습니다.',
        { variant: 'success' });
      onChanged?.(groupUuid === NONE ? '' : groupUuid);
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.message || '바꾸지 못했습니다.', { variant: 'error' });
      setSelected(value || NONE);         // 실패하면 되돌린다
    }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    if (v === NEW) {
      setDialogOpen(true);
      return;                             // 선택은 그대로 두고 창만 연다
    }
    setSelected(v);
    apply(v);
  };

  const handleCreated = async (created) => {
    await load();
    if (created?.groupUuid) {
      setSelected(created.groupUuid);
      apply(created.groupUuid);
    }
  };

  return (
    <Box>
      <TextField
        select
        value={selected}
        onChange={handleChange}
        size="small"
        fullWidth
        disabled={disabled || !prjUuid}
        helperText={!prjUuid ? '기회를 저장한 뒤에 묶을 수 있습니다' : ''}
      >
        <MenuItem value={NONE}>
          <Typography variant="body2" color="text.secondary">지정 안 함</Typography>
        </MenuItem>
        {groups.map((g) => (
          <MenuItem key={g.groupUuid} value={g.groupUuid}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
              <span>{g.groupNm}</span>
              <Typography variant="caption" color="text.secondary">
                {g.groupTypNm || g.groupTyp} · {g.chanceCnt ?? 0}건
              </Typography>
            </Box>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem value={NEW}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}>
            <AddIcon fontSize="inherit" /> 새 묶음 만들기
          </Box>
        </MenuItem>
      </TextField>

      <GroupFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={handleCreated}
      />
    </Box>
  );
};

export default GroupPicker;
